import { useEffect, useMemo, useState, type SyntheticEvent } from 'react';
import {
  SCENARIO_PERIOD_UNITS,
  scenarioFigures,
  scenarioPeriods,
  type DealScenarioDetail,
} from '@forecastify/shared';
import {
  Button,
  Select,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TextInput,
  cn,
} from '@forecastify/ui';
import { useI18n, useT } from '../../i18n';
import { useDuration } from '../../duration';
import { DateField } from '../date-field';
import { dealMoney } from './deal-kit';
import { parseAmount } from './plan-kit';
import {
  amountField,
  monthLabel,
  monthsPerPeriod,
  pctText,
  periodUnitLabel,
  scenarioInput,
  signedPct,
  type ScenarioWrite,
} from './scenario-kit';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 20 Oct 2026 · Phase 6 · D9 · a recurring scenario's step 2 — `07g`. DS-2. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The schedule's four settings, then a row per period from `scenarioPeriods`: the template's
 * revenue × `(1 + index%)^year`, or a figure typed over it (*kézi*, reset back to the indexed one).
 *
 * ⚠️ **A pick saves; a typed number waits for Enter or *Mentés*** (`state-rules.md` §8). The first
 * period and the period length are discrete choices and PATCH at once; the count and the index are
 * typed, and a write per keystroke would race its own version.
 */

export function ScenarioSchedule({
  detail,
  withOptions,
  readOnly,
  write,
}: {
  readonly detail: DealScenarioDetail;
  readonly withOptions: boolean;
  readonly readOnly: boolean;
  readonly write: ScenarioWrite;
}) {
  const t = useT();
  const { fmt } = useDuration();
  const { locale } = useI18n();
  const currency = detail.currencyCode;
  const [count, setCount] = useState(String(detail.periodCount));
  const [index, setIndex] = useState('');
  const [revenueDrafts, setRevenueDrafts] = useState<ReadonlyMap<number, string>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const savedIndex = amountField(Number(detail.indexPct), locale);
  /* A live reload replaces the settings unless they are being typed (`state-rules.md` §11). */
  useEffect(() => {
    setCount(String(detail.periodCount));
  }, [detail.periodCount]);
  useEffect(() => {
    setIndex(savedIndex);
  }, [savedIndex]);

  const input = useMemo(() => scenarioInput(detail, withOptions), [detail, withOptions]);
  const periods = useMemo(() => scenarioPeriods(input), [input]);
  const figures = useMemo(() => scenarioFigures(input), [input]);
  const template = periods[0];
  const perYear = Math.max(
    1,
    Math.round(12 / monthsPerPeriod(detail.periodUnit, detail.periodStep)),
  );

  const patch = async (body: Record<string, unknown>): Promise<boolean> => {
    setError(null);
    setBusy(true);
    const refusal = await write('', 'PATCH', { version: detail.version, ...body });
    setBusy(false);
    if (refusal !== null) {
      setError(refusal);
      return false;
    }
    return true;
  };

  const dirty = count !== String(detail.periodCount) || index !== savedIndex;
  const saveSettings = async (event?: SyntheticEvent): Promise<void> => {
    event?.preventDefault();
    if (!dirty || busy) return;
    const periodCount = Number(count);
    if (!Number.isInteger(periodCount) || periodCount < 1 || periodCount > 120) {
      setError(t('dealScenario.error.periodCount'));
      return;
    }
    const indexPct = Number(index.replace(/\s/gu, '').replace('%', '').replace(',', '.'));
    if (index.trim() === '' || !Number.isFinite(indexPct) || indexPct < -50 || indexPct > 100) {
      setError(t('dealScenario.error.index'));
      return;
    }
    await patch({ periodCount, indexPct });
  };

  const saveRevenue = async (at: number): Promise<void> => {
    const draft = revenueDrafts.get(at);
    if (draft === undefined) return;
    const value = parseAmount(draft);
    if (value === null) {
      setError(t('deals.error.value'));
      return;
    }
    const period = periods[at];
    const unchanged = value === period?.revenue;
    if (!unchanged) {
      setError(null);
      const refusal = await write(`/overrides/${String(at)}`, 'PUT', { revenue: value });
      if (refusal !== null) {
        setError(refusal);
        return;
      }
    }
    setRevenueDrafts((current) => {
      const next = new Map(current);
      next.delete(at);
      return next;
    });
  };

  const reset = async (at: number): Promise<void> => {
    setError(null);
    const refusal = await write(`/overrides/${String(at)}`, 'PUT', { revenue: null });
    if (refusal !== null) setError(refusal);
  };

  return (
    <div className="flex min-w-0 flex-col">
      <form
        className="flex flex-wrap items-end gap-4 border-b px-4 py-3"
        onSubmit={(event) => void saveSettings(event)}
      >
        <label className="flex flex-col gap-1">
          <span className="text-muted-foreground text-[11.5px]">
            {t('dealScenario.schedule.start')}
          </span>
          <DateField
            className="w-[150px]"
            value={detail.startsOn}
            clearable={false}
            disabled={readOnly || busy}
            label={t('dealScenario.schedule.start')}
            onChange={(value) => {
              if (value !== '' && value !== detail.startsOn) void patch({ startsOn: value });
            }}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-muted-foreground text-[11.5px]">
            {t('dealScenario.schedule.unit')}
          </span>
          <Select
            className="w-[150px]"
            value={detail.periodUnit}
            disabled={readOnly || busy}
            aria-label={t('dealScenario.schedule.unit')}
            onChange={(event) => {
              void patch({ periodUnit: event.target.value });
            }}
            options={SCENARIO_PERIOD_UNITS.map((unit) => ({
              value: unit,
              label: periodUnitLabel(t, unit),
            }))}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-muted-foreground text-[11.5px]">
            {t('dealScenario.schedule.count')}
          </span>
          <Stepper
            className="w-[120px]"
            value={count}
            min={1}
            max={120}
            disabled={readOnly}
            aria-label={t('dealScenario.schedule.count')}
            decrementLabel={t('dealScenario.schedule.countLess')}
            incrementLabel={t('dealScenario.schedule.countMore')}
            onChange={(value) => {
              setCount(value);
              setError(null);
            }}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-muted-foreground text-[11.5px]">
            {t('dealScenario.schedule.index')}
          </span>
          <div className="relative w-[110px]">
            <TextInput
              inputMode="decimal"
              readOnly={readOnly}
              className="pr-7 text-right tabular-nums"
              value={index}
              aria-label={t('dealScenario.schedule.index')}
              onChange={(event) => {
                setIndex(event.target.value);
                setError(null);
              }}
            />
            <span className="text-muted-foreground pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2 text-[12.5px]">
              %
            </span>
          </div>
        </label>
        {dirty && !readOnly ? (
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setCount(String(detail.periodCount));
                setIndex(savedIndex);
                setError(null);
              }}
            >
              {t('dealScenario.discard')}
            </Button>
            <Button type="submit" size="sm" disabled={busy}>
              {t('common.save')}
            </Button>
          </div>
        ) : null}
        <p className="text-muted-foreground min-w-[260px] flex-1 basis-full text-[12px] leading-relaxed xl:basis-0">
          {t('dealScenario.schedule.hint', {
            revenue: dealMoney(template?.indexed ?? 0, currency),
            hours: fmt(template?.hours ?? 0),
            count: perYear,
          })}
        </p>
        {error === null ? null : (
          <p className="text-destructive basis-full text-[12px]" role="alert">
            {error}
          </p>
        )}
      </form>

      <Table label={t('dealScenario.schedule.label')} className="w-max min-w-full">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="min-w-[150px] pl-4">
              {t('dealScenario.schedule.period')}
            </TableHead>
            <TableHead className="min-w-[160px] text-right">
              {t('dealScenario.grid.revenue')}
            </TableHead>
            <TableHead className="min-w-[140px] text-right">
              {t('dealScenario.schedule.labour')}
            </TableHead>
            <TableHead className="min-w-[110px] text-right">
              {t('dealScenario.grid.hours')}
            </TableHead>
            <TableHead className="min-w-[150px] text-right">
              {t('dealScenario.grid.margin')}
            </TableHead>
            <TableHead className="min-w-[100px] text-right">
              {t('dealScenario.compare.marginPct')}
            </TableHead>
            <TableHead className="min-w-[140px]">{t('dealScenario.schedule.change')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {periods.map((period) => {
            const margin = period.revenue - period.labour;
            const draft = revenueDrafts.get(period.index);
            const label = monthLabel(period.startsOn, locale);
            return (
              <TableRow key={period.index}>
                <TableCell className="pl-4">
                  <span className="text-muted-foreground mr-2 inline-block w-5 text-right text-[11.5px] tabular-nums">
                    {period.index + 1}
                  </span>
                  {label}
                </TableCell>
                <TableCell className="px-1 py-1">
                  <input
                    aria-label={t('dealScenario.schedule.revenueOf', { period: label })}
                    readOnly={readOnly}
                    inputMode="decimal"
                    value={draft ?? amountField(period.revenue, locale)}
                    onChange={(event) => {
                      const value = event.target.value;
                      setError(null);
                      setRevenueDrafts((current) => new Map(current).set(period.index, value));
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        void saveRevenue(period.index);
                      } else if (event.key === 'Escape') {
                        setRevenueDrafts((current) => {
                          const next = new Map(current);
                          next.delete(period.index);
                          return next;
                        });
                      }
                    }}
                    className={cn(
                      'hover:ring-input focus:ring-ring/50 h-7 w-full rounded-sm bg-transparent px-2 text-right text-[12.5px] tabular-nums outline-none hover:ring-1 focus:ring-2 read-only:hover:ring-0',
                      period.overridden && 'text-primary font-medium',
                      draft !== undefined && 'bg-accent/60',
                    )}
                  />
                </TableCell>
                <TableCell numeric className="text-muted-foreground">
                  {dealMoney(period.labour, currency)}
                </TableCell>
                <TableCell numeric className="text-muted-foreground">
                  {fmt(period.hours)}
                </TableCell>
                <TableCell numeric className={cn(margin < 0 && 'text-destructive')}>
                  {dealMoney(margin, currency)}
                </TableCell>
                <TableCell numeric className={cn(margin < 0 && 'text-destructive')}>
                  {pctText(period.revenue > 0 ? margin / period.revenue : null, locale)}
                </TableCell>
                <TableCell className="py-1">
                  {period.overridden ? (
                    <span className="flex items-center gap-1.5">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-primary h-6 px-1.5 text-[11.5px]"
                        disabled={readOnly}
                        title={t('dealScenario.schedule.reset')}
                        aria-label={t('dealScenario.schedule.resetOf', { period: label })}
                        onClick={() => void reset(period.index)}
                      >
                        {t('dealScenario.schedule.manual')}
                      </Button>
                      {period.indexed > 0 ? (
                        <span className="text-muted-foreground text-[11.5px] tabular-nums">
                          {signedPct(period.revenue / period.indexed - 1, locale)}
                        </span>
                      ) : null}
                    </span>
                  ) : period.multiplier !== 1 ? (
                    <span className="text-muted-foreground text-[11.5px] tabular-nums">
                      {signedPct(period.multiplier - 1, locale)}
                    </span>
                  ) : null}
                </TableCell>
              </TableRow>
            );
          })}
          <TableRow className="border-t-2 font-medium hover:bg-transparent">
            <TableCell className="pl-4">{t('dealScenario.schedule.total')}</TableCell>
            <TableCell numeric className="pr-3">
              {dealMoney(figures.revenue, currency)}
            </TableCell>
            <TableCell numeric>{dealMoney(figures.labour, currency)}</TableCell>
            <TableCell numeric>{fmt(figures.hours)}</TableCell>
            <TableCell numeric className={cn(figures.margin < 0 && 'text-destructive')}>
              {dealMoney(figures.margin, currency)}
            </TableCell>
            <TableCell numeric className={cn(figures.margin < 0 && 'text-destructive')}>
              {pctText(figures.marginPct, locale)}
            </TableCell>
            <TableCell className="text-muted-foreground text-[11.5px] font-normal">
              {figures.external > 0
                ? t('dealScenario.schedule.external', {
                    amount: dealMoney(figures.external, currency),
                  })
                : ''}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}
